// src/pages/user/profile.js

import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import { useLanguage } from "../../contexts/languageContext";
import PasswordInput from "../../components/form/passwordInput";
import LanguageChangeModal from "../../components/common/LanguageChangeModal";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";


// Simple date formatter function
const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export default function Profile() {
  const { t } = useTranslation();
  useLanguage(); // Hook needed for language context
  const { updatePageTitle } = useMainPageTitle();
  const { user, updateUser } = useAuth();
  
  useEffect(() => {
    updatePageTitle(t('profile.title'), t('profile.subtitle'));
  }, [updatePageTitle, t]);

  // Sync userData with auth context user
  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);
  
  const [userData, setUserData] = useState(user || JSON.parse(localStorage.getItem("user")));
  const [profileImage, setProfileImage] = useState(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditingImage, setIsEditingImage] = useState(false);

  useEffect(() => {
    if (userData.profilePicture) {
      // Use direct URL since profilePicture is already a complete URL
      const fullImageUrl = userData.profilePicture;
      setProfileImage(fullImageUrl);
    }
  }, [userData.profilePicture]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview the selected image
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    setSelectedImage(file);
    setIsEditingImage(true);
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;

    console.log("🖼️ [Frontend] Starting image upload");
    console.log("🖼️ [Frontend] Selected image:", selectedImage);
    console.log("🖼️ [Frontend] API URL:", API_URL);

    const formData = new FormData();
    formData.append('profilePicture', selectedImage);

    console.log("🖼️ [Frontend] FormData created, file appended");

    try {
      console.log("🖼️ [Frontend] Making request to:", `${API_URL}/user/set-profile-picture`);
      
      const response = await fetch(`${API_URL}/user/set-profile-picture`, {
        method: 'PUT',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log("🖼️ [Frontend] Response status:", response.status);
      console.log("🖼️ [Frontend] Response ok:", response.ok);

      const data = await response.json();
      console.log("🖼️ [Frontend] Response data:", data);

      if (response.ok) {
        console.log("✅ [Frontend] Upload successful");
        
        // Use direct URL since profilePicture is already a complete URL
        const fullImageUrl = data.profilePicture;
        console.log("🖼️ [Frontend] New image URL:", fullImageUrl);
        
        setProfileImage(fullImageUrl);
        const updatedUser = { ...userData, profilePicture: data.profilePicture };
        
        // Update auth context (this will also update localStorage)
        updateUser(updatedUser);
        setUserData(updatedUser);
        setSuccess(t("messages.profilePictureUpdatedSuccessfully"));
        setIsEditingImage(false);
        setSelectedImage(null);
        setImagePreview(null);
        
        // Remove the page reload to see console logs and improve UX
        // window.location.reload();
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      } else {
        console.error("❌ [Frontend] Upload failed:", data);
        setError(data.message || t("messages.failedToUpdateProfilePicture"));
      }
    } catch (error) {
      console.error('❌ [Frontend] Error updating profile picture:', error);
      setError(t("messages.anErrorOccurredWhileUpdatingProfilePicture"));
    }
  };

  const handleCancelImageEdit = () => {
    setIsEditingImage(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Handle language change
  const handleLanguageChange = (newLanguage) => {
    const updatedUser = { 
      ...userData, 
      metaData: { 
        ...userData.metaData, 
        preferences: { 
          ...userData.metaData?.preferences, 
          language: newLanguage 
        } 
      } 
    };
    
    // Update auth context (this will also update localStorage)
    updateUser(updatedUser);
    setUserData(updatedUser);
  };

  // Get current language from user data
  const getCurrentLanguage = () => {
    return userData?.metaData?.preferences?.language || "en";
  };

  // Add these modal components inside Profile component
  const EmailChangeModal = () => {
    const [newEmail, setNewEmail] = useState("");

    const handleEmailChange = async (e) => {
      e.preventDefault();
      try {
        const response = await fetch(`${API_URL}/user/change-email`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ email: newEmail })
        });

        const data = await response.json();

        if (response.ok) {
          const updatedUser = { ...userData, email: newEmail };
          
          // Update auth context (this will also update localStorage)
          updateUser(updatedUser);
          setUserData(updatedUser);
          setSuccess(t("messages.emailUpdatedSuccessfully"));
          setIsEditingEmail(false);
          // change token
          localStorage.setItem('token', data.token);
        } else {
          setError(data.message || t("messages.failedToUpdateEmail"));
        }
      } catch (error) {
        setError(t("messages.anErrorOccurredWhileUpdatingEmail"));
      }
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-xl w-96">
          <h3 className="text-lg font-semibold mb-4">Change Email</h3>
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">New Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsEditingEmail(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Update Email
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const PhoneChangeModal = () => {
    const [newPhone, setNewPhone] = useState("");

    const handlePhoneChange = async (e) => {
      e.preventDefault();
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError("You are not authenticated. Please log in again.");
        return;
      }

      try {
        const response = await fetch(`${API_URL}/user/change-phone-number`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ phone: newPhone })
        });

        const data = await response.json();

        if (response.ok) {
          const updatedUser = { ...userData, phone: newPhone };
          
          // Update auth context (this will also update localStorage)
          updateUser(updatedUser);
          setUserData(updatedUser);
          setSuccess(t("messages.phoneUpdatedSuccessfully"));
          setIsEditingPhone(false);
        } else {
          if (response.status === 401) {
            setError("Your session has expired. Please log in again.");
            // Optionally, redirect to login page here
          } else {
            setError(data.message || t("messages.failedToUpdatePhoneNumber"));
          }
        }
      } catch (error) {
        console.error('Error updating phone:', error);
        setError(t("messages.anErrorOccurredWhileUpdatingPhoneNumber"));
      }
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-xl w-96">
          <h3 className="text-lg font-semibold mb-4">Change Phone</h3>
          <form onSubmit={handlePhoneChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">New Phone</label>
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsEditingPhone(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Update Phone
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const PasswordChangeModal = () => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const handlePasswordChange = async (e) => {
      e.preventDefault();
      if (newPassword !== confirmPassword) {
        setError(t("messages.newPasswordsDontMatch"));
        return;
      }

      try {
        const response = await fetch(`${API_URL}/user/change-password`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            oldPassword: currentPassword,
            newPassword: newPassword,
            confirmPassword: confirmPassword
          })
        });

        const data = await response.json();

        if (response.ok) {
          setSuccess(t("messages.passwordUpdatedSuccessfully"));
          setIsChangingPassword(false);
        } else {
          setError(data.message || t("messages.failedToUpdatePassword"));
        }
      } catch (error) {
        setError(t("messages.anErrorOccurredWhileUpdatingPassword"));
      }
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-xl w-96">
          <h3 className="text-lg font-semibold mb-4">Change Password</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <PasswordInput
                label={t("labels.currentPassword")}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <PasswordInput
                label={t("labels.newPassword")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <PasswordInput
                label={t("labels.confirmNewPassword")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsChangingPassword(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {t("profile.updatePassword")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-600 rounded-md">
          {success}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Profile Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800">{t("profile.profileSettings")}</h1>
        </div>

        {/* Profile Image Section */}
        <div className="flex items-center mb-8 space-x-6">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100">
              {isEditingImage && imagePreview ? (
                                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : profileImage ? (
                  <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    onError={() => setProfileImage(null)}
                  />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <span className="text-4xl text-gray-400">
                    {userData.firstName?.charAt(0)}{userData.lastName?.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            <label className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors">
              <input 
                type="file" 
                className="hidden" 
                accept="image/*"
                onChange={handleImageSelect}
              />
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </label>
          </div>
          <div className="flex flex-col space-y-2">
            <h2 className="text-2xl font-semibold text-gray-800">
              {userData.firstName} {userData.lastName}
            </h2>
            <p className="text-gray-600">@{userData.userName}</p>
            {isEditingImage && (
              <div className="flex space-x-2">
                <button
                  onClick={handleImageUpload}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {t("common.save")}
                </button>
                <button
                  onClick={handleCancelImageEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {t("common.cancel")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profile Details */}
        <div className="space-y-6">
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">{t("profile.firstName")}</label>
              <p className="p-3 bg-gray-50 rounded-md text-gray-800">{userData.firstName}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">{t("profile.lastName")}</label>
              <p className="p-3 bg-gray-50 rounded-md text-gray-800">{userData.lastName}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">{t("profile.userName")}</label>
              <p className="p-3 bg-gray-50 rounded-md text-gray-800">{userData.userName}</p>
            </div>
            {userData.accountType === 'player' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">{t("profile.birthday")}</label>
                <p className="p-3 bg-gray-50 rounded-md text-gray-800">
                  {formatDate(userData.metaData?.birthday)}
                </p>
              </div>
            )}
          </div>

          {/* Contact Info Section */}
          <div className="space-y-6 pt-6 border-t">
            <h3 className="text-lg font-semibold text-gray-800">{t("profile.contactInformation")}</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-600">{t("profile.email")}</label>
                <button 
                  onClick={() => setIsEditingEmail(!isEditingEmail)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t("profile.changeEmail")}
                </button>
              </div>
              <p className="p-3 bg-gray-50 rounded-md text-gray-800">{userData.email}</p>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-600">{t("profile.phone")}</label>
                <button 
                  onClick={() => setIsEditingPhone(!isEditingPhone)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t("profile.changePhone")}
                </button>
              </div>
              <p className="p-3 bg-gray-50 rounded-md text-gray-800">{userData.phone}</p>
            </div>
          </div>

          {/* Security Section */}
          <div className="space-y-6 pt-6 border-t">
            <h3 className="text-lg font-semibold text-gray-800">{t("profile.security")}</h3>
            <button 
              onClick={() => setIsChangingPassword(!isChangingPassword)}
              className="w-full md:w-auto px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              {t("profile.changePassword")}
            </button>
          </div>

          {/* Language Settings Section */}
          <div className="space-y-6 pt-6 border-t">
            <h3 className="text-lg font-semibold text-gray-800">{t("profile.languageAndRegion")}</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-600">{t("profile.language")}</label>
                <button 
                  onClick={() => setIsChangingLanguage(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t("profile.changeLanguage")}
                </button>
              </div>
              <div className="p-3 bg-gray-50 rounded-md text-gray-800 flex items-center">
                <span className="text-lg mr-3">
                  {getCurrentLanguage() === "ar" ? "🇸🇦" : "🇺🇸"}
                </span>
                <span>
                  {getCurrentLanguage() === "ar" ? t("messages.arabic") : t("messages.english")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditingEmail && <EmailChangeModal />}
      {isEditingPhone && <PhoneChangeModal />}
      {isChangingPassword && <PasswordChangeModal />}
      
      <LanguageChangeModal
        isOpen={isChangingLanguage}
        onClose={() => setIsChangingLanguage(false)}
        currentLanguage={getCurrentLanguage()}
        onLanguageChange={handleLanguageChange}
        onSuccess={setSuccess}
        onError={setError}
      />
    </div>
  );
}
